import type { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { repository } from '@whizz/api-client';
import { QUERIES_IDS } from '../../../../../../util/vars';
import { renderMandrillTemplate } from '../../../../../../helper/handlebars';
import theme from '../../../../../../styles/theme';
import { useI18n } from '@whizz/react-i18n';

const EMAIL_TEMPLATE = {
  'en-gb': 'parents-weekly-update-en-gb',
  'en-us': 'parents-weekly-update-en-us',
  'en-nz': 'parents-weekly-update-en-nz',
  'en-ke': 'parents-weekly-update-en-ke',
  'es-mx': 'parents-weekly-update-es-mx',
  'sw-ke': 'parents-weekly-update-sw-ke',
  'th-th': 'parents-weekly-update-th-th',
};

const EmailPreview = ({ previewData }: Props): JSX.Element => {
  const { locale } = useI18n();
  const localeId = locale?.id ?? 'en-gb';
  const template = EMAIL_TEMPLATE[localeId as keyof typeof EMAIL_TEMPLATE] ?? EMAIL_TEMPLATE['en-gb'];

  const getMailchimpRenderedTemplateQuery = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_MAILCHIMP_RENDERED_TEMPLATE, { template, locale: localeId } ],
    queryFn: () => repository.AdminRepository.getMailchimpRenderedTemplate(template, { locale: localeId }),
  });

  useEffect(() => {
    const emailPreview = document.getElementById('email-preview');
    if (emailPreview) {
      // Find the sponsorship image element
      const sponsorshipElement = emailPreview.querySelector('img[src="' + previewData.sponsorshipImage + '"]');
      if (sponsorshipElement) {
        emailPreview.scrollTo(0, 500);
      }
    }
  }, [ getMailchimpRenderedTemplateQuery.isSuccess, previewData.sponsorshipImage ]);

  return (
    <div className={'relative flex justify-center'} style={{ filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.5))' }}>
      {(getMailchimpRenderedTemplateQuery.isSuccess) && (
        <>
          <div className={'w-full border-black bg-black overflow-hidden absolute transform scale-50 origin-top'} style={{ borderRadius: 60, borderWidth: 16, width: 600, height: 1000, top: -50 }}>
            <div className={'flex absolute justify-center'} style={{ width: 570 }}>
              <div className={'rounded-b-full bg-black w-28 h-4'} />
            </div>
            <div className={'w-full -mt-2 overflow-y-scroll overflow-x-hidden'} style={{ height: 1000 }} id={'email-preview'}>
              <div
                dangerouslySetInnerHTML={{
                  __html: renderMandrillTemplate(
                    getMailchimpRenderedTemplateQuery.data.data,
                    {
                      fullName: previewData.fullName,
                      firstName: previewData.firstName,
                      startDate: previewData.startDate?.toFormat('dd MMMM'),
                      endDate: previewData.endDate?.toFormat('dd MMMM'),
                      progressionsHexColor: theme.colors.positive,
                      progressionsCount: 3,
                      progressionsPosition: 238,
                      minutesHexColor: theme.colors.positive,
                      minutesCount: 60,
                      minutesPosition: 356,
                      isInHallOfFame: true,
                      weeksInHallOfFame: 3,
                      sponsorship: previewData.sponsorshipImage,
                      sponsorshipLink: previewData.sponsorshipLink,
                    },
                  ),
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};


interface Props {
  previewData: PreviewData;
}

export interface PreviewData {
  isEnabled: boolean;
  fullName: string;
  firstName: string;
  startDate: DateTime | null;
  endDate: DateTime | null;
  sponsorshipImage: string | null;
  sponsorshipLink: string | null;
}

export { EmailPreview };
